import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC, useTRPCClient } from "@/trpc";
import type { RouterInputs } from "@/trpc";
import { toast } from "sonner";

type SubmitWeekEntriesInput =
  RouterInputs["timeEntry"]["submitWeekTimeEntries"];
type ConfirmScheduledHoursInput =
  RouterInputs["timeEntry"]["confirmScheduledHours"];
type ReportShiftIssueInput = RouterInputs["timeEntry"]["reportShiftIssue"];

/**
 * Hook for submitting time entries with optimistic updates.
 */
export function useSubmitTimeEntries() {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SubmitWeekEntriesInput) =>
      trpcClient.timeEntry.submitWeekTimeEntries.mutate(input),
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
      });
    },
    onSuccess: (data) => {
      toast.success(
        `Time entries submitted for ${data.entriesCreated} shift${data.entriesCreated !== 1 ? "s" : ""}`,
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to submit time entries");
    },
    onSettled: () => {
      // Refetch to ensure data is in sync
      queryClient.invalidateQueries({
        queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
      });
    },
  });
}

/**
 * Hook for quick submit (confirm scheduled hours).
 */
export function useConfirmScheduledHours() {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ConfirmScheduledHoursInput) =>
      trpcClient.timeEntry.confirmScheduledHours.mutate(input),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
      });
    },
    onSuccess: (data) => {
      toast.success(
        `Hours confirmed for ${data.entriesCreated} shift${data.entriesCreated !== 1 ? "s" : ""}`,
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to confirm hours");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
      });
    },
  });
}

/**
 * Removes a scheduled shift from the timesheet queue after the provider
 * reports that they could not attend or that the schedule is incorrect.
 */
export function useReportShiftIssue() {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ReportShiftIssueInput) =>
      trpcClient.timeEntry.reportShiftIssue.mutate(input),
    onSuccess: (_data, input) => {
      toast.success(
        input.issueType === "unable_to_attend"
          ? "Shift marked as not worked"
          : "Shift issue reported",
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to report the shift issue");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
      });
    },
  });
}
